import { getEddConfig } from '@/data/config/eddConfig';
import { HomeDeliveryDateState, useHomeDeliveryDate } from '@/data/Content/HomeDeliveryDate';
import { useSettings } from '@/data/Settings';
import { ProductType } from '@/data/types/Product';
import { ProductAvailabilityData } from '@/data/types/ProductAvailabilityData';
import { resolveFreeShipping } from '@/data/utils/eddFreeShipping';
import { ComboStock, getPdpChannelAvailability } from '@/data/utils/eddInventory';
import { useMemo } from 'react';
import { isMarketFulfilledProduct } from 'shared/custom/utils/inventoryUtils';
import { TYPES } from '../constants/product';
import { isPickupOnlyEnabled } from '../utils/eddProductFlags';

/**
 * `HomeDeliveryDateState` plus the one status 159071 AC10/AC11 introduces.
 * Deliberately NOT added to the shared `HomeDeliveryDateState` type in
 * `HomeDeliveryDate.ts` — that type is also consumed by the PLP label component,
 * which has no rendering of its own for a counter-only option and shouldn't need
 * to know about a status its own hook never produces. Scoped to this file instead;
 * `useHomeDeliveryDate` itself never returns it.
 */
type EffectiveHomeDeliveryDateState = HomeDeliveryDateState | { status: 'available-no-date' };

export type PdpHomeDeliveryOption = EffectiveHomeDeliveryDateState & {
	/** AC5 (159042) / AC24-AC29 (159071): market units available. Omitted whenever
	 * the option is disabled. */
	availableQuantity?: number;
	/** AC9 (delivery-options story): present (and always `true`) only once the
	 * offer price clears the threshold. */
	freeShipping?: true;
	/** 159071 AC19-AC20: the delivery cutoff time storeconf, for the footer legend
	 * when this option is the one currently selected. Omitted when disabled. */
	cutoffTime?: string;
	/** 159071 AC21-AC22: whether the footnote marker belongs next to this option's
	 * date line — true only for `with-date` (the only status with a date line whose
	 * value depended on the cutoff check). Omitted when disabled. */
	hasCutoffFootnote?: boolean;
	/** Dropship / Special Order: the footer legend reads "order today" instead of
	 * naming a cutoff time. Omitted when disabled. */
	isMarketFulfilled?: boolean;
};

const isVisible = (status: EffectiveHomeDeliveryDateState['status']): boolean =>
	status === 'with-date' || status === 'available-no-date' || status === 'same-day';

const derivePdpComboStock = (
	isCombo: boolean,
	marketAvailability: number
): ComboStock | undefined => {
	if (!isCombo) return undefined;
	if (marketAvailability >= 1) return 'all-in-market';
	return 'incomplete';
};

/**
 * PDP home-delivery option (delivery-options story AC1-AC3/AC5/AC9; 159071
 * AC10-AC12, AC19-AC23, AC24/AC29/AC30).
 *
 * AC1/AC2: eligibility, cutoff, and date calculation are the exact same
 * `useHomeDeliveryDate` story 159042's PLP label already calls — home delivery
 * eligibility never depended on inventory (only the zip-block list and leadtime),
 * so unlike pickup there is no separate resolver to extract; the hook is reused
 * whole. This layer only adds fields on top, plus the one-line "upgrade" 159071
 * AC10 introduces (see `deriveEffectiveState`) — it does not touch, and cannot
 * change, what `useHomeDeliveryDate` itself decides.
 */

/**
 * 159071 AC10/AC11: `no-label` collapses two different causes in the base hook —
 * no leadtime, or the AC20 (158621) misconfiguration limit — neither of which
 * checks market stock, because delivery eligibility never depended on it before
 * this story. If the product is otherwise deliverable (the base hook already
 * ruled out blocked-zip and bopisOnly to reach `no-label`) AND the market has
 * stock, 159071 wants that surfaced as a still-selectable, counter-only option
 * rather than hidden — exactly the `available-no-date` pickup already has
 * natively. A `no-label` with zero market stock is genuinely unavailable and
 * stays hidden.
 */
const deriveEffectiveState = (
	state: HomeDeliveryDateState,
	marketAvailability: number,
	isCombo: boolean,
	/** Only meaningful when `isCombo` is true. */
	comboStock: ComboStock | undefined
): EffectiveHomeDeliveryDateState => {
	if (isCombo) {
		return resolveComboHomeDeliveryState(state, comboStock ?? 'incomplete');
	}
	return state.status === 'no-label' && marketAvailability > 0
		? { status: 'available-no-date' }
		: state;
};

const resolveComboHomeDeliveryState = (
	state: HomeDeliveryDateState,
	stock: ComboStock
): EffectiveHomeDeliveryDateState => {
	if (stock === 'incomplete') return { status: 'no-label' };
	return state;
};

export const usePdpHomeDeliveryOption = (
	product: ProductType | undefined,
	physicalStoreId: string,
	storeAvail: ProductAvailabilityData | undefined,
	offerPrice: number | undefined
): PdpHomeDeliveryOption => {
	const { storeAvailability, marketAvailability } = useMemo(
		() => getPdpChannelAvailability(storeAvail),
		[storeAvail]
	);
	// Same Day (163024 AC11/AC24, 163146 AC4): the PDP's store inventory comes from
	// the availability call, not the search index. Callers overwrite
	// `availableQuantity` with the market count for Dropship/Special Order (162524),
	// so it no longer is the store count for them; they hold no sales-floor stock
	// (163024 AC14), hence 0.
	const state = useHomeDeliveryDate(product, physicalStoreId, {
		checkMarketAvailability: false,
		storeAvailability: isMarketFulfilledProduct(product) ? 0 : storeAvailability,
	});
	const { settings } = useSettings();

	return useMemo(() => {
		const config = getEddConfig(settings.userData);

		// Combo availability story, AC2(b)/AC4: delivery is available for a combo
		// only if at least one component isn't itself flagged bopisOnly -- the
		// "every component has inventory" half of AC2 is already carried by
		// `marketAvailability` above (the blended, min-across-components counter
		// that already drives `deriveEffectiveState`'s no-label upgrade), so this
		// is purely the catalog-attribute half. `every()` on an empty array is
		// vacuously true, hence the length guard.
		const isCombo = product?.type === TYPES.kit;
		const comboStock = derivePdpComboStock(isCombo, marketAvailability);

		const effectiveState = deriveEffectiveState(state, marketAvailability, isCombo, comboStock);
		const kitComponents = product?.components ?? [];
		const comboBlocksDelivery = isCombo && kitComponents.length > 0 && isPickupOnlyEnabled(config);

		if (comboBlocksDelivery) {
			return { status: 'not-available' };
		}

		// Zero market stock means the option is sold out regardless of date
		// eligibility — disable with "Agotado" instead of showing "0 disponibles".
		if (isVisible(effectiveState.status) && marketAvailability <= 0) {
			return { status: 'no-label' };
		}

		// AC30 (159071) / AC5 (delivery-options story): a disabled option carries
		// none of the extra fields — pending/blocked-zip/not-available/no-label.
		if (!isVisible(effectiveState.status)) {
			return effectiveState;
		}

		return {
			...effectiveState,
			availableQuantity: marketAvailability,
			// AC11: "available-no-date" still counts as available for delivery, so
			// free shipping is still evaluated for it, not only for `with-date`.
			freeShipping: resolveFreeShipping(offerPrice, config.freeShippingThreshold),
			// 163024 AC4: Same Day has its own cutoff, independent from home delivery's.
			cutoffTime:
				effectiveState.status === 'same-day' && config.sameDay
					? config.sameDay.cutoffTime
					: config.deliveryCutoffTime,
			// AC21: only `with-date` has a date line to attach the marker to;
			// `available-no-date` shows no "Entrega estimada..." line at all (AC10).
			// 163024: a Same Day date depends on its own cutoff check just the same. Past
			// that cutoff the date ignores the home delivery cutoff, so no legend at all.
			hasCutoffFootnote:
				(effectiveState.status === 'with-date' && !effectiveState.sameDayPastCutoff) ||
				effectiveState.status === 'same-day',
			isMarketFulfilled: isMarketFulfilledProduct(product),
		};
	}, [state, marketAvailability, offerPrice, settings.userData, product]);
};
